export type VideoSource = {
  src: string;
  type?: string; // e.g., "video/mp4", "video/webm"
};

export type PlanMedia =
  | { kind: "image"; src: string; alt?: string }
  | {
      kind: "video";
      poster?: string;
      sources: VideoSource | VideoSource[]; // one or multiple sources
      autoPlay?: boolean;
      loop?: boolean;
      muted?: boolean;
      playsInline?: boolean;
    };

export type Project = {
  slug: string;
  title: string;
  cover: string;
  year?: string;
  location?: string;
  scope?: string;
  description?: string;
  gallery: string[];
  client?: string;
  type?: string;
  status?: string;

  // ✅ New flexible media array (preferred way going forward)
  plansMedia?: PlanMedia[];
  isometryMedia?: PlanMedia[];

  // ✅ Legacy fields (still supported for backward compatibility)
  plansVideo?: string | string[]; // single or multiple videos
  plans?: string; // single plan image
  mapEmbed?: string; // site location iframe embed URL
};

export const projects: Project[] = [
  {
    slug: "i-land-961",
    title: "961 (NINE SIX ONE)",
    cover: "/images/I-Land-961/I1.webp",
    location: "Biel - Beirut",
    scope: "Design",
    type: "Tourism & Entertainments",
    description:
      "I-Land is a floating luxury complex that blends modern architecture with resort living, featuring a central pool, sun decks, and dining areas to create a unique destination for leisure and entertainment on the water.",
    gallery: [
      "/images/I-Land-961/I1.webp",
      "/images/I-Land-961/I2.webp",
      "/images/I-Land-961/I3.webp",
      "/images/I-Land-961/I4.webp",
      "/images/I-Land-961/I5.webp",
      "/images/I-Land-961/I6.jpg",
      "/images/I-Land-961/I7.webp",
      "/images/I-Land-961/I8.webp",
      "/images/I-Land-961/I9.webp",
    ],
  },
  {
    slug: "ashrafieh-871",
    title: "Ashrafieh 871",
    cover: "/images/Ashrafieh-871/ash1.webp",
    location: "Ashrafieh- Beirut",
    scope: "Design",
    type: "Residential building",
    gallery: [
      "/images/Ashrafieh-871/ash1.webp",
      "/images/Ashrafieh-871/ash2.webp",
      "/images/Ashrafieh-871/ash3.webp",
    ],
  },
  {
    slug: "kz-villa",
    title: "KZ - Villa",
    cover: "/images/KZ/kz1.webp",
    location: "Kayfoun - Aley",
    scope: "Design & Build",
    type: "Private Residence",
    gallery: [
      "/images/KZ/kz1.webp",
      "/images/KZ/kz2.webp",
      "/images/KZ/kz3.webp",
    ],
    plansMedia: [
      {
        kind: "image",
        src: "/images/KZ/01.jpg",
        alt: "KZ - Villa – Plan 01",
      },
      {
        kind: "image",
        src: "/images/KZ/02.jpg",
        alt: "KZ - Villa – Plan 02",
      },
      {
        kind: "image",
        src: "/images/KZ/03.jpg",
        alt: "KZ - Villa – Plan 03",
      },
    ],
  },
  {
    slug: "ah-residence",
    title: "AH Residence",
    cover: "/images/AH/Ah1.webp",
    location: "Louweizeh - Baabda",
    scope: "Design & Supervision",
    type: "Private Residence",
    gallery: [
      "/images/AH/Ah1.webp",
      "/images/AH/Ah2.webp",
      "/images/AH/Ah3.webp",
      "/images/AH/Ah4.webp",
      "/images/AH/Ah5.jpg",
      "/images/AH/Ah6.webp",
    ],
    plansMedia: [
      {
        kind: "image",
        src: "/images/AH/01.png",
        alt: "AH Residence – Plan 01",
      },
      {
        kind: "image",
        src: "/images/AH/02.png",
        alt: "AH Residence – Plan 02",
      },
      {
        kind: "image",
        src: "/images/AH/03.png",
        alt: "AH Residence – Plan 03",
      },
    ],
  },
  {
    slug: "lumley-beach-tower",
    title: "Lumley Beach Tower",
    cover: "/images/lumley/lml1.webp",
    location: "Lumley Beach - Sierra Leone",
    scope: "Design",
    type: "Residential Building",
    gallery: [
      "/images/lumley/lml1.webp",
      "/images/lumley/lml2.webp",
      "/images/lumley/lml3.webp",
      "/images/lumley/lml4.webp",
      "/images/lumley/lml5.webp",
      "/images/lumley/lml6.webp",
    ],
    plansMedia: [
      {
        kind: "image",
        src: "/images/lumley/Groundfloor.jpg",
        alt: "Lumley Beach Tower – Ground floor plan",
      },
      {
        kind: "image",
        src: "/images/lumley/typicalfloor.jpg",
        alt: "Lumley Beach Tower – Typical floor plan",
      },
    ],
  },
  {
    slug: "lh-residences",
    title: "LH Residences",
    cover: "/images/LHResidences/lh1.webp",
    location: "Souk El Gharb - Aley",
    scope: "Design",
    type: "Residential Bldg.",
    gallery: [
      "/images/LHResidences/lh1.webp",
      "/images/LHResidences/lh2.webp",
      "/images/LHResidences/lh3.webp",
      "/images/LHResidences/lh4.webp",
      "/images/LHResidences/lh5.webp",
    ],
    plansMedia: [
      {
        kind: "image",
        src: "/images/LHResidences/01.png",
        alt: "LH Residences – Plan 01",
      },
      {
        kind: "image",
        src: "/images/LHResidences/02.png",
        alt: "LH Residences – Plan 02",
      },
      {
        kind: "image",
        src: "/images/LHResidences/03.png",
        alt: "LH Residences – Plan 03",
      },
    ],
  },
  {
    slug: "otwelve",
    title: "OTWELVE",
    cover: "/images/address/a1.webp",
    location: "El Jnah - Beirut",
    scope: "Interior Design & Build",
    type: "Commercial",
    gallery: [
      "/images/address/a1.webp",
      "/images/address/a2.webp",
      "/images/address/a3.webp",
      "/images/address/a4.webp",
      "/images/address/a5.webp",
    ],
    plansMedia: [
      {
        kind: "image",
        src: "/images/address/plan.png",
        alt: "OTWELVE – plan",
      },
    ],
  },
  {
    slug: "atelier-harouki",
    title: "Atelier Harouki",
    cover: "/images/harouki/Scene3.webp",
    location: "Sanayeh - Beirut",
    scope: "Interior Design & Supervision",
    type: "Commercial",
    gallery: [
      "/images/harouki/Scene3.webp",
      "/images/harouki/Scene4.webp",
      "/images/harouki/Scene5.webp",
      "/images/harouki/Scene10.webp",
    ],
    plansMedia: [
      {
        kind: "image",
        src: "/images/harouki/Plan.jpg",
        alt: "Atelier Harouki – plan",
      },
    ],
  },
  {
    slug: "huawei-head-office",
    title: "Huawei Head Office",
    cover: "/images/huawei/h1.webp",
    location: "Aberdeen - Sierra Leone",
    scope: "Interior Design",
    type: "Commercial",
    gallery: [
      "/images/huawei/h1.webp",
      "/images/huawei/h2.webp",
      "/images/huawei/h3.webp",
      "/images/huawei/h4.webp",
      "/images/huawei/h5.webp",
    ],
    plansMedia: [
      {
        kind: "image",
        src: "/images/huawei/Huawei Sierra Leone plan.jpg",
        alt: "Huawei Head Office – plan",
      },
    ],
  },
  {
    slug: "sierra-market",
    title: "Sierra Market",
    cover: "/images/sierramarket/sm1.webp",
    location: "Freetown - Sierra Leone",
    scope: "Design",
    type: "Commercial Bldg.",
    gallery: [
      "/images/sierramarket/sm1.webp",
      "/images/sierramarket/sm2.webp",
      "/images/sierramarket/sm3.webp",
      "/images/sierramarket/sm4.webp",
      "/images/sierramarket/sm5.webp",
    ],
    plansMedia: [
      {
        kind: "image",
        src: "/images/sierramarket/Supermarket-Sierra-Leone-06--ground-floor.jpg",
        alt: "Sierra Market – Ground floor plan",
      },
      {
        kind: "image",
        src: "/images/sierramarket/Supermarket-Sierra-Leone-06-FIRST-FLOOR.jpg",
        alt: "Sierra Market – First floor plan",
      },
    ],
  },
  {
    slug: "spurloop-villas",
    title: "Spurloop Villas",
    cover: "/images/spurloop/cover1.webp",
    location: "Spurloop - Sierra Leone",
    scope: "Design",
    type: "Private Residence",
    gallery: [
      "/images/spurloop/cover1.webp",
      "/images/spurloop/magnifics_upscale-A2EU7axTA7abkc3sOtWZ-NEX_01_2 - Photo.webp",
      "/images/spurloop/magnifics_upscale-AcKxJr1BITj3acAXMxvh-NEX_01_9 - Photo.webp",
      "/images/spurloop/magnifics_upscale-VE8bZijVFasV9ijRrJH7-NEX_01_7 - Photo.webp",
      "/images/spurloop/magnifics_upscale-WfjFCdkd6osfcxgMwN7V-NEX_01_8 - Photo.webp",
    ],
    plansMedia: [
      {
        kind: "image",
        src: "/images/spurloop/villa-1 plans.jpg",
        alt: "Spurloop Villas – Plan 1",
      },
      {
        kind: "image",
        src: "/images/spurloop/villa-2 plans.jpg",
        alt: "Spurloop Villas – Plan 2",
      },
      {
        kind: "image",
        src: "/images/spurloop/villa-3 plans.jpg",
        alt: "Spurloop Villas – Plan 3",
      },
    ],
  },
   {
    "slug": "fr-residence",
    "title": "FR Residence",
    "cover": "/images/Ainab/A6.webp",
    "year": "2026",
    "location": "Ainab - Aley",
    "scope": "Design & Supervision",
    "type": "Private Residence - Renovation",
    "status": "Delivered",
    "description": "This project renovates a traditional Lebanese house in Ainab, Mount Lebanon, preserving its heritage while introducing a refined contemporary touch. The design maintains the original architectural identity—stone façade, proportions, and spatial character—while integrating modern elements to enhance functionality and comfort. The triplex villa reflects a balanced dialogue between past and present, where tradition is carefully reinterpreted rather than replicated.Designed and supervised by Nextudio.",
    "gallery": [
      "/images/Ainab/A6.webp",
      "/images/Ainab/A5.webp",
      "/images/Ainab/A4.webp",
      "/images/Ainab/A3.webp",
      "/images/Ainab/A2.webp",
      "/images/Ainab/A1.webp",
      "/images/Ainab/A7.webp"
    ],
    "plansMedia": [
      {
        "kind": "image",
        "src": "/images/Ainab/basement1plan.jpg",
        "alt": "FR Residence – Basement plan"
      },
      {
        "kind": "image",
        "src": "/images/Ainab/groundfloorplan.jpg",
        "alt": "FR Residence – Ground floor plan"
      },
      {
        "kind": "image",
        "src": "/images/Ainab/1stfloorplan.jpg",
        "alt": "FR Residence – First floor plan"
      }
    ]
  },
  {
    slug: "rs-btater-villa-02",
    title: "RS Btater Villa 02",
    cover: "/images/Rs-btater-villa-02/rs-btater.webp", //hero image
    year: "2022",
    location: "Btater, Lebanon",
    status: "Under Construction",
    client: "R.Sebai",
    type: "Housing",
    description:
      "A modern retreat seamlessly integrated into its natural surroundings, featuring sustainable design elements and open \n" +
      " spaces that embrace the landscape.",
    gallery: [
      "/images/Rs-btater-villa-02/rs-btater-villa-02-1.webp",
      "/images/Rs-btater-villa-02/rs-btater-villa-02-2.webp",
      "/images/Rs-btater-villa-02/rs-btater-villa-02-3.jpg",
    ],
    plansMedia: [
      {
        kind: "image",
        src: "/images/Rs-btater-villa-02/Rs-plan.webp",
        alt: "RS Btater Villa 02 – plan",
      },
    ],
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d1581.5687775992049!2d35.61624914943825!3d33.76425402370655!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2slb!4v1701300593578!5m2!1sen!2slb", // ← new
  },
  {
    slug: "raad-apartment",
    title: "Raad Apartment",
    cover: "/images/Raad-Apartment/raad.webp", // main hero image
    year: "2023",
    location: "Sakiet El Janzir | Beirut - Lebanon",
    status: "Delivered",
    client: "M.Raad",
    type: "Interior Design",
    description:
      "A warm, contemporary apartment with layered lighting, crafted wood finishes, and calm neutral tones.",
    gallery: [
      "/images/Raad-Apartment/raad-apartment01.webp",
      "/images/Raad-Apartment/raad-apartment02.webp",
      "/images/Raad-Apartment/raad-apartment03.webp",
      "/images/Raad-Apartment/raad-apartment04.webp",
      "/images/Raad-Apartment/raad-apartment05.webp",
      "/images/Raad-Apartment/raad-apartment06.webp",
      "/images/Raad-Apartment/raad-apartment07.webp",
      "/images/Raad-Apartment/raad-apartment08.webp",
      "/images/Raad-Apartment/raad-apartment09.webp",
      "/images/Raad-Apartment/raad-apartment10.webp",
      "/images/Raad-Apartment/raad-apartment11.webp",
      "/images/Raad-Apartment/raad-apartment12.webp",
      "/images/Raad-Apartment/raad-apartment13.webp",
      "/images/Raad-Apartment/raad-apartment14.webp",
      "/images/Raad-Apartment/raad-apartment15.webp",
    ],
    plansMedia: [
      {
        kind: "image",
        src: "/images/Raad-Apartment/raad-apartment-plan.webp",
        alt: "Raad Apartment – plan",
      },
    ],
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d626.7217191279755!2d35.478858939181315!3d33.89026420486429!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2slb!4v1701300804150!5m2!1sen!2slb",
  },
  {
    slug: "chemlan-residence-renovation",
    title: "Chemlan Residence Renovation",
    cover: "/images/Chemlane-Residences/chemlan-residence.webp", // main hero image
    year: "2023",
    location: "Chemlan, Lebanon",
    status: "Delivered",
    client: "D.Dweik",
    type: "Renovation Housing",
    description:
      "A modern renovation of a traditional residence, blending contemporary design with the charm of its original architecture.",
    gallery: [
      "/images/Chemlane-Residences/chemlan-residence01.webp",
      "/images/Chemlane-Residences/chemlan-residence02.webp",
      "/images/Chemlane-Residences/chemlan-residence03.webp",
      "/images/Chemlane-Residences/chemlan-residence04.webp",
      "/images/Chemlane-Residences/chemlan-residence05.webp",
      "/images/Chemlane-Residences/chemlan-residence06.webp",
    ],

    // Use the new flexible field to show BOTH (video first, then image)
    plansMedia: [
      {
        kind: "image",
        src: "/images/Chemlane-Residences/chemlan-residence-plan-2.webp",
        alt: "Chemlan Residence – plan",
      },
    ],
    isometryMedia: [
      {
        kind: "image",
        src: "/images/Chemlane-Residences/plansc.webp",
        alt: "Chemlan Residence Renovation - plan 2",
      },
    ],

    // (Do NOT include legacy `plansVideo` or `plans` here)
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d790.6393108732792!2d35.547172225139214!3d33.77998102102648!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2slb!4v1701299291036!5m2!1sen!2slb",
  },
  {
    slug: "baawerta-nests",
    title: "Baawerta Nests",
    cover: "/images/BAAWRETA/baawerta.jpg", // main hero
    year: "2023",
    location: "Baawerta, Lebanon",
    status: "Preliminary Design",
    client: "F.Al-Mahmoud",
    type: "Resorts",
    description:
      "A unique residential project featuring modern architecture and sustainable design, nestled in the heart of Baawerta.",
    gallery: [
      "/images/BAAWRETA/baawerta01.webp",
      "/images/BAAWRETA/baawerta02.webp",
      "/images/BAAWRETA/baawerta03.webp",
      "/images/BAAWRETA/baawerta04.webp",
      "/images/BAAWRETA/baawerta05.webp",
      "/images/BAAWRETA/baawerta06.webp",
      "/images/BAAWRETA/baawerta07.webp",
      "/images/BAAWRETA/baawerta08.webp",
      "/images/BAAWRETA/baawerta09.webp",
    ],
    isometryMedia: [
      {
        kind: "image",
        src: "/images/BAAWRETA/baawreta-plan1.webp",
        alt: "Baawerta Nests – plan 1",
      },
    ],

    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d790.9831747129698!2d35.50034202722971!3d33.742711179750316!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2slb!4v1701298831446!5m2!1sen!2slb",
  },
  {
    slug: "al-maktoum-tower-al-fujaira",
    title: "Al Maktoum Tower | Al Fujaira",
    cover: "/images/Maktoum/maktoum.webp", // main hero image
    year: "2023",
    location: "Al Fujaira, UAE",
    status: "Preliminary Design",
    client: "T.Nawfal",
    type: "Residential building",
    description:
      "A landmark tower in Al Fujaira, blending modern architecture with traditional elements, offering stunning views \n" +
      "and luxurious amenities.",

    gallery: [
      "/images/Maktoum/maktoum01.webp",
      "/images/Maktoum/maktoum02.webp",
      "/images/Maktoum/maktoum03.webp",
    ],
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d2048.3971281778886!2d56.3214175904039!3d25.12214731530521!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2slb!4v1701297967727!5m2!1sen!2slb",
  },
  {
    slug: "ak-apartment",
    title: "AK Apartment",
    cover: "/images/AK-APARTEMENT/ak-apartment1.jpg", // main hero image
    year: "2023",
    location: "Beirut, Lebanon",
    status: "Completed",
    client: "A.Khalil",
    type: "Interior Design",
    description:
      "A contemporary apartment with a focus on open spaces, natural light, and modern finishes, creating a serene living environment.",
    gallery: [
      "/images/AK-APARTEMENT/ak-apartment01.webp",
      "/images/AK-APARTEMENT/ak-apartment02.webp",
      "/images/AK-APARTEMENT/ak-apartment03.webp",
      "/images/AK-APARTEMENT/ak-apartment04.webp",
      "/images/AK-APARTEMENT/ak-apartment05.webp",
    ],
    plansMedia: [
      {
        kind: "image",
        src: "/images/AK-APARTEMENT/AK-Apartment2-plan.webp",
        alt: "AK Apartment – plan 2",
      },
    ],

    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d1182.9482283279976!2d35.47319518750655!3d33.900654958361706!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2slb!4v1701297676345!5m2!1sen!2slb",
  },

  {
    slug: "sariin-villa-renovation",
    title: "Sariin Villa Renovation", // main hero image
    cover: "/images/Sariin/sariin2.jpg",
    year: "2022",
    location: "Sariin, Lebanon",
    status: "Preliminary Design",
    client: "M.Farhat",
    type: "Renovation Housing",
    description:
      "A comprehensive renovation of a villa in Sariin, blending modern design with the villa's original character, enhancing \n" +
      "both aesthetics and functionality.",
    gallery: [
      "/images/Sariin/sariin01.webp",
      "/images/Sariin/sariin02.webp",
      "/images/Sariin/sariin03.webp",
      "/images/Sariin/sariin04.webp",
      "/images/Sariin/sariin05.webp",
      "/images/Sariin/sariin06.webp",
      "/images/Sariin/sariin07.webp",
    ],
    isometryMedia: [
      {
        kind: "image",
        src: "/images/Sariin/plans-3-ezgif.com-gif-to-webp-converter.webp",
        alt: "Sariin Villa Renovation – plan",
      },
    ],
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d3756.747230293284!2d36.06616143160788!3d33.875245170160696!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2slb!4v1701297078362!5m2!1sen!2slb",
  },
  {
    slug: "kfr-426 Villa",
    title: "KFR-426 | Landscape",
    cover: "/images/KFR-426/kfr-426.webp", // main hero image
    year: "2022",
    location: "Kfarfakoud, Lebanon",
    status: "Completed",
    client: "H&M Weiss",
    type: "Residential Housing",
    description:
      "A landscape project in Kfarfakoud, focusing on sustainable design and integration with the natural environment, enhancing \n" +
      " the area's beauty and ecological balance.",
    gallery: [
      "/images/KFR-426/kfr-04.webp",
      "/images/KFR-426/kfr-02.webp",
      "/images/KFR-426/kfr-03.webp",
      "/images/KFR-426/kfr-01.webp",
      "/images/KFR-426/kfr-05 (1).jpg",
      "/images/KFR-426/kfr-06.webp",
      "/images/KFR-426/kfr-07.webp",
      "/images/KFR-426/kfr-08.webp",
      "/images/KFR-426/kfr-09.webp",
      "/images/KFR-426/kfr-10.webp",
      "/images/KFR-426/kfr-12.webp",
      "/images/KFR-426/kfr-13.webp",
      "/images/KFR-426/kfr-14.webp",
      "/images/KFR-426/kfr-15.webp",
    ],
    plansMedia: [
      {
        kind: "image",
        src: "/images/KFR-426/Kfr 247  - Ground Floor.webp",
        alt: "KFR-426 – Ground floor plan",
      },
      {
        kind: "image",
        src: "/images/KFR-426/Kfr 247 - First Floor.webp",
        alt: "KFR-426 – First floor plan",
      },
    ],
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d1336.3565508984188!2d35.67026980501038!3d33.34822242772227!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2slb!4v1701296687686!5m2!1sen!2slb",
  },
  {
    slug: "jarjouaa-1418",
    title: "Jarjouaa 1418",
    cover: "/images/JARJOUA-1418/jarjouaa-01.webp", // main hero image
    year: "2022",
    location: "Jarjouaa, Lebanon",
    status: "Preliminary Design",
    client: "D.Dweik",
    type: "Residential Housing",
    description:
      "A residential project in Jarjouaa, combining modern architecture with local traditions, creating a unique living space.",
    gallery: [
      "/images/JARJOUA-1418/jarjouaa-01.webp",
      "/images/JARJOUA-1418/jarjouaa-02.webp",
      "/images/JARJOUA-1418/jarjouaa-03.webp",
      "/images/JARJOUA-1418/jarjouaa-04.webp",
      "/images/JARJOUA-1418/jarjouaa-05.webp",
    ],
    isometryMedia: [
      {
        kind: "image",
        src: "/images/JARJOUA-1418/jarjouaa-plans.webp",
        alt: "Jarjouaa 1418 – plan 1",
      },
    ],
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d840.8698896461092!2d35.51979464468172!3d33.44962427885062!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2slb!4v1701296164348!5m2!1sen!2slb",
  },
  {
    slug: "hemadeh-apartement",
    title: "Hemadeh Apartement",
    cover: "/images/Hemade-Apartment/hemadeh-01.jpg", // main hero image
    year: "2022",
    location: "Beirut, Lebanon",
    status: "Delivered",
    client: "N.Hemadeh",
    type: "Interior Design",
    description:
      "A modern apartment in Beirut, designed with a focus on comfort and contemporary aesthetics, featuring open spaces and elegant finishes.",
    gallery: [
      "/images/Hemade-Apartment/hemadeh-01.jpg",
      "/images/Hemade-Apartment/hemadeh-02.jpg",
      "/images/Hemade-Apartment/hemadeh-03.jpg",
      "/images/Hemade-Apartment/hemadeh-04.jpg",
      "/images/Hemade-Apartment/hemadeh-05.jpg",
      "/images/Hemade-Apartment/hemadeh-06.jpg",
      "/images/Hemade-Apartment/hemadeh-07.jpg",
      "/images/Hemade-Apartment/hemadeh-08.jpg",
    ],
    plansMedia: [
      {
        kind: "image",
        src: "/images/Hemade-Apartment/hemadeh-plan.jpg",
        alt: "Hemadeh Apartment – plan 1",
      },
    ],
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d745.3733234705978!2d35.48472525790523!3d33.88209415691871!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2slb!4v1701295880741!5m2!1sen!2slb",
  },
  {
    slug: "b-twin-villas",
    title: "B-Twin Villas",
    cover: "/images/B-TWIN-Villa/b-twin-villas.webp", // main
    year: "2022",
    location: "Bechtlida | Jbeil - Lebanon",
    status: "Preliminary",
    client: "Y.Hamdar",
    type: "Residential Housing",
    description:
      "A pair of modern villas in Beirut, designed with a focus on luxury and comfort",
    gallery: [
      "/images/B-TWIN-Villa/b-twin-villas-01.webp",
      "/images/B-TWIN-Villa/b-twin-villas-02.webp",
      "/images/B-TWIN-Villa/b-twin-villas-03.webp",
      "/images/B-TWIN-Villa/b-twin-villas-04.webp",
      "/images/B-TWIN-Villa/b-twin-villas-05.webp",
    ],
    isometryMedia: [
      {
        kind: "image",
        src: "/images/B-TWIN-Villa/villas-twin-plan.webp",
        alt: "B-Twin Villas – plan 1",
      },
    ],
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d1668.3963580509605!2d35.72371568126484!3d34.13163979283509!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2slb!4v1701295367649!5m2!1sen!2slb",
  },
  {
    slug: "ns-btater-villa",
    title: "NS BTATER VILLA",
    cover: "/images/NS-Btater-villa/ns-btater-villa.webp", // main hero image
    year: "2022",
    location: "Btater, Lebanon",
    status: "Preliminary",
    client: "N.Sebai",
    type: "Residential Housing",
    description:
      "A modern villa in Btater, designed with a focus on luxury and comfort, featuring spacious interiors and elegant finishes.",
    gallery: [
      "/images/NS-Btater-villa/ns-btater-villa-01.webp",
      "/images/NS-Btater-villa/ns-btater-villa-02.webp",
      "/images/NS-Btater-villa/ns-btater-villa-03.webp",
      "/images/NS-Btater-villa/ns-btater-villa-04.webp",
      "/images/NS-Btater-villa/ns-btater-villa-05.webp",
    ],
    isometryMedia: [
      {
        kind: "image",
        src: "/images/NS-Btater-villa/ns-btater-villa-plan.webp",
        alt: "NS Btater Villa – plan 1",
      },
      {
        kind: "image",
        src: "/images/NS-Btater-villa/ns-btater-villa-plan-2.webp",
        alt: "NS Btater Villa – plan 2",
      },
    ],
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d1581.5687775992049!2d35.61624914943825!3d33.76425402370655!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2slb!4v1701295051234!5m2!1sen!2slb",
  },
  {
    slug: "wa-yarzeh",
    title: "WA-Yarzeh",
    cover: "/images/Wa-Yarzeh/wa-yarzeh.webp",
    year: "2021",
    location: "Yarzeh, Lebanon",

    status: "Delivered",
    client: "WA",
    type: "Interior Design",
    description:
      "A modern residential project in Yarzeh, designed with a focus on luxury and comfort, featuring spacious interiors and elegant finishes.",
    gallery: [
      "/images/Wa-Yarzeh/wa-yarzeh-01.webp",
      "/images/Wa-Yarzeh/wa-yarzeh-02.webp",
      "/images/Wa-Yarzeh/wa-yarzeh-03.webp",
      "/images/Wa-Yarzeh/wa-yarzeh-04.webp",
      "/images/Wa-Yarzeh/wa-yarzeh-05.webp",
      "/images/Wa-Yarzeh/wa-yarzeh-06.webp",
      "/images/Wa-Yarzeh/wa-yarzeh-07.webp",
    ],

    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d1580.3442539489472!2d35.545276608535026!3d33.83055367725569!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2slb!4v1701293736337!5m2!1sen!2slb",
  },
  {
    slug: "tokeh-nest-resort",
    title: "Tokeh Nest Resort",
    cover: "/images/Tokeh-Nest-Resort/tokeh-nest-resort.webp", // main hero image
    year: "2020",
    location: "Tokeh, Sierra Leone",
    status: "Preliminary",
    client: "D&B Construction Limited",
    type: "Resorts",
    gallery: [
      "/images/Tokeh-Nest-Resort/tokeh-nest-resort-01.webp",
      "/images/Tokeh-Nest-Resort/tokeh-nest-resort-02.webp",
      "/images/Tokeh-Nest-Resort/tokeh-nest-resort-03.webp  ",
      "/images/Tokeh-Nest-Resort/tokeh-nest-resort-04.webp  ",
      "/images/Tokeh-Nest-Resort/tokeh-nest-resort-05.webp  ",
      "/images/Tokeh-Nest-Resort/tokeh-nest-resort-06.webp  ",
    ],
    plansMedia: [
      {
        kind: "image",
        src: "/images/Tokeh-Nest-Resort/tokeh-nest-resort-plan-3.webp",
        alt: "Tokeh Nest Resort – plan 3",
      },
    ],
    isometryMedia: [
      {
        kind: "image",
        src: "/images/Tokeh-Nest-Resort/tokeh-nest-resort-plan.webp",
        alt: "Tokeh Nest Resort – plan 1",
      },
      {
        kind: "image",
        src: "/images/Tokeh-Nest-Resort/tokeh-nest-resort-plan-2.webp",
        alt: "Tokeh Nest Resort – plan 2",
      },
    ],
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d3166.004119048364!2d-13.190912779590223!3d8.301911796731776!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2slb!4v1701292763862!5m2!1sen!2slb",
  },
  {
    slug: "heinrich-boell-beirut-offices",
    title: "Heinrich Boell Beirut Offices",
    cover: "/images/Heinrich-Boell-Beirut/heinrich-boell-beirut.webp", // main hero image
    year: "2020",
    location: "Adlieh | Beirut- Lebanon",
    status: "Delivered",
    client: "Heinrich Boell Foundation",
    type: "Interior Design build",
    description:
      "A modern office space in Beirut, designed with a focus on functionality and aesthetics, featuring open spaces and contemporary finishes.",
    gallery: [
      "/images/Heinrich-Boell-Beirut/heinrich-boell-beirut-01.webp",
      "/images/Heinrich-Boell-Beirut/heinrich-boell-beirut-02.webp",
      "/images/Heinrich-Boell-Beirut/heinrich-boell-beirut-03.webp",
      "/images/Heinrich-Boell-Beirut/heinrich-boell-beirut-04.webp",
      "/images/Heinrich-Boell-Beirut/heinrich-boell-beirut-05.webp",
    ],

    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d626.7974870023437!2d35.5231326561046!3d33.87995086438847!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x151f16fc00925317%3A0x140a27c2a4b1b397!2sHeinrich%20B%C3%B6ll%20Stiftung!5e1!3m2!1sen!2slb!4v1701291118141!5m2!1sen!2slb",
  },
  {
    slug: "guodrich-offices",
    title: "Guodrich Offices",
    cover: "/images/Guodrich-Offices/guodrich-offices.webp", // main hero image
    year: "2023",
    location: "Freetown | Sierra Leone",
    status: "Preliminary Design",
    client: "D&B Construction Limited",
    type: "Commercial",
    description:
      "A modern office space in Beirut, designed with a focus on functionality and aesthetics, featuring open spaces and contemporary finishes.",
    gallery: [
      "/images/Guodrich-Offices/guodrich-offices-01.webp",
      "/images/Guodrich-Offices/guodrich-offices-02.webp",
      "/images/Guodrich-Offices/guodrich-offices-03.webp",
      "/images/Guodrich-Offices/guodrich-offices-04.webp",
    ],
    plansMedia: [
      {
        kind: "image",
        src: "/images/Guodrich-Offices/guodrich-offices-plan.webp",
        alt: "Guodrich Offices – plan 1",
      },
    ],
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d940.9388845757571!2d-13.290783147949524!3d8.434204758563823!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2slb!4v1701290635639!5m2!1sen!2slb",
  },
  {
    title: "Sierra Motel",
    slug: "sierra-motel",
    cover: "/images/Sierra-Motel/sierra-motel.webp", // main hero image
    year: "2019",
    location: "Sierra Leone",
    status: "Preliminary",
    client: "D&B Construction Limited",
    type: "Hotels",
    description:
      "A modern motel in Sierra Leone, designed with a focus on comfort and convenience, featuring spacious rooms and contemporary amenities.",
    gallery: [
      "/images/Sierra-Motel/sierra-motel-01.webp",
      "/images/Sierra-Motel/sierra-motel-02.webp",
    ],
    plansMedia: [
      {
        kind: "image",
        src: "/images/Sierra-Motel/sierraplans.webp",
        alt: "Sierra Motel – plan 1",
      },
    ],
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d986.8039214005022!2d-13.243609055459824!3d8.380433844376693!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2slb!4v1701290368162!5m2!1sen!2slb",
  },
  {
    slug: "the-valley",
    title: "The Valley",
    cover: "/images/The-Valley/the-valley.webp", // main hero image
    year: "2018",
    location: "Aberdeen , Sierra Leone",
    status: "Under Construction",
    client: "D&B Construction Limited",
    type: "Residential Complex",
    description:
      "A modern residential project, designed with a focus on luxury and comfort, featuring  spacious interiors and elegant finishes.",
    gallery: [
      "/images/The-Valley/the-valley-01.webp",
      "/images/The-Valley/the-valley-02.webp",
      "/images/The-Valley/the-valley-03.webp",
      "/images/The-Valley/the-valley-04.webp",
      "/images/The-Valley/the-valley-05.webp",
      "/images/The-Valley/the-valley-06.webp",
      "/images/The-Valley/the-valley-07.webp",
      "/images/The-Valley/the-valley-08.webp",
      "/images/The-Valley/the-valley-09.webp",
      "/images/The-Valley/the-valley-10.webp",
      "/images/The-Valley/the-valley-11.webp",
    ],
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d1944.5869872794274!2d-13.28833525294448!3d8.494431239942223!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2slb!4v1701090456515!5m2!1sen!2slb",
  },
  {
    slug: "sugarme",
    title: "Sugarme",
    cover: "/images/Sugarme/sugarme-02.webp", // main hero image
    year: "2018",
    location: "Hazmieh , Beirut | Lebanon",
    status: "Delivered",
    client: "J&D Jabbour",
    type: "Interior Design build",
    description:
      "A modern residential project in Beirut, designed with a focus on luxury and comfort, featuring spacious interiors  and elegant finishes.",
    gallery: [
      "/images/Sugarme/sugarme-01.webp",
      "/images/Sugarme/sugarme-02.webp",
      "/images/Sugarme/sugarme-03.webp",
      "/images/Sugarme/sugarme-04.webp",
      "/images/Sugarme/sugarme-05.webp",
      "/images/Sugarme/sugarme-06.webp",
      "/images/Sugarme/sugarme-07.webp",
    ],
    isometryMedia: [
      {
        kind: "image",
        src: "/images/Sugarme/sugarme-plan.webp",
        alt: "Sugarme – plan 1",
      },
    ],
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d343.2203488305608!2d35.52872768410255!3d33.863027497784614!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x151f17c947c0687b%3A0x4dc393c28078db7!2sSugarmelb!5e1!3m2!1sen!2slb!4v1701089790291!5m2!1sen!2slb",
  },
  {
    slug: "pitshop",
    title: "Pitshop",
    cover: "/images/Pit-Shop/pitshop.webp", // main hero image
    year: "2018",
    location: "Freetown - Sierra Leone",
    status: "Preliminary Design",
    client: "D&B Construction Limited",
    type: "Commercial",
    description:
      "A modern commercial project in Freetown, designed with a focus on functionality and aesthetics, featuring open spaces \n" +
      " and contemporary finishes.",
    gallery: [
      "/images/Pit-Shop/pitshop-01.webp",
      "/images/Pit-Shop/pitshop-02.webp",
      "/images/Pit-Shop/pitshop-03.webp",
    ],
    isometryMedia: [
      {
        kind: "image",
        src: "/images/Pit-Shop/pitshop-plan.webp",
        alt: "Pitshop – plan 1",
      },
    ],

    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1190.183862781189!2d-13.270572228809657!3d8.4800508444455!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xf04c256b3e60633%3A0x97b7c8c8af013c1e!2sFreetown%2C%20Sierra%20Leone!5e1!3m2!1sen!2slb!4v1701089436722!5m2!1sen!2slb",
  },
  {
    slug: "key-f1-residence",
    title: "Key-F1 Residence",
    cover: "/images/Key-F1-residence/key-f1-residence.webp",
    year: "2018",
    location: "Kayfoun , Mount Lebanon",
    status: "Delivered",
    client: "M.Omar",
    type: "Residential building Build",
    gallery: [
      "/images/Key-F1-residence/keyf-01.webp",
      "/images/Key-F1-residence/keyf-02.webp",
      "/images/Key-F1-residence/keyf-03.webp",
    ],
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d687.1363136999063!2d35.55593746591892!3d33.77640451143056!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2slb!4v1701088151561!5m2!1sen!2slb",
  },
  {
    slug: "hall-of-honor-sierra-leone",
    title: "Hall of Honor-Sierra Leone",
    cover: "/images/Hall-Of-Honor/hall-of-honor.webp",
    year: "2018",
    location: "Freetown, Sierra Leone",
    client: "D&B Construction Limited",
    type: "Governmental",
    status: "preliminary Design",
    gallery: [
      "/images/Hall-Of-Honor/hall-01.webp",
      "/images/Hall-Of-Honor/hall-02.webp",
      "/images/Hall-Of-Honor/hall-03.webp",
      "/images/Hall-Of-Honor/hall-04.webp",
      "/images/Hall-Of-Honor/hall-05.webp",
    ],
    plansMedia: [
      {
        kind: "image",
        src: "/images/Hall-Of-Honor/hall-plan1.webp",
        alt: "Hall of Honor-Sierra Leone – plan 1",
      },
      {
        kind: "image",
        src: "/images/Hall-Of-Honor/hall-plan2.webp",
        alt: "Hall of Honor-Sierra Leone – plan 2",
      },
    ],
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d1943.9531260364745!2d-13.188618927079483!3d8.618579545464316!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2slb!4v1701087746228!5m2!1sen!2slb",
  },
  {
    slug: "aberdeen-residences",
    title: "Aberdeen Residences",
    cover: "/images/Aberdeen-Residences/aberdeen-residences.webp",
    year: "2018",
    location: "Aberdeen , Sierra Leone",
    client: "D&B Construction Limited",
    type: "Residential building",
    status: "preliminary Design",
    gallery: [
      "/images/Aberdeen-Residences/A1.webp",
      "/images/Aberdeen-Residences/A2.webp",
      "/images/Aberdeen-Residences/A3.webp",
      "/images/Aberdeen-Residences/A4.webp",
    ],
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d1375.0197958015654!2d-13.296645283944851!3d8.49745769081672!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2slb!4v1701084767032!5m2!1sen!2slb",
  },
  {
    slug: "al-ain-residences",
    title: "Al AIN RESIDENCES",
    cover: "/images/AL-Ain-Residences/al-ain-residences.webp",
    year: "2018",
    location: "Marwaniyyi , South Lebanon",
    status: "Delivered",
    type: "Residential building Build",
    gallery: [
      "/images/AL-Ain-Residences/Ain-1.webp",
      "/images/AL-Ain-Residences/Ain-2.webp",
      "/images/AL-Ain-Residences/Ain-3.webp",
      "/images/AL-Ain-Residences/Ain-4.webp",
      "/images/AL-Ain-Residences/Ain-5.webp",
      "/images/AL-Ain-Residences/Ain-6.webp",
      "/images/AL-Ain-Residences/Ain-7.webp",
      "/images/AL-Ain-Residences/Ain-8.webp",
    ],
    isometryMedia: [
      {
        kind: "image",
        src: "/images/AL-Ain-Residences/ainplan1.webp",
        alt: "Al AIN RESIDENCES - plan 1",
      },
    ],
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d689.7345798088929!2d35.387754798673015!3d33.4511022078773!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2slb!4v1701077259259!5m2!1sen!2slb",
  },
  {
    slug: "togo-school",
    title: "Togo School",
    cover: "/images/Togo-School/tg3.webp",
    year: "2017",
    location: "Lumi , Togo",
    client: "H.Khatib",
    type: "Educational building",
    status: "Preliminary",
    gallery: [
      "/images/Togo-School/tg1.webp",
      "/images/Togo-School/tg2.webp",
      "/images/Togo-School/tg3.webp",
      "/images/Togo-School/tg4.webp",
    ],
    plansMedia: [
      {
        kind: "image",
        src: "/images/Togo-School/tgp1.jpg",
        alt: "Togo-School - plan 1",
      },
      {
        kind: "image",
        src: "/images/Togo-School/tgp2.jpg",
        alt: "Togo-School - plan 2",
      },
    ],
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d1785.3194573548512!2d1.2424102479529608!3d6.156575874658891!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sar!2slb!4v1701024891332!5m2!1sar!2slb",
  },
  {
    slug: "supermarket-furnished-apartment",
    title: "Supermarket & Furnished Apt",
    cover: "/images/SuperMarket/supermarket.webp",
    year: "2017",
    location: "Sierra Leone",
    client: "D&B Construction Limited",
    type: "Commercial",
    status: "preliminary Design",
    gallery: [
      "/images/SuperMarket/Sf1.webp",
      "/images/SuperMarket/Sf2.webp",
      "/images/SuperMarket/Sf3.webp",
      "/images/SuperMarket/Sf4.webp",
      "/images/SuperMarket/Sf5.webp",
      "/images/SuperMarket/Sf6.webp",
    ],
    plansMedia: [
      {
        kind: "image",
        src: "/images/SuperMarket/sfp1.webp",
        alt: "Supermarket & Furnished Apt - plan 1",
      },

      {
        kind: "image",
        src: "/images/SuperMarket/sfp2.webp",
        alt: "Supermarket & Furnished Apt - plan 2",
      },
      {
        kind: "image",
        src: "/images/SuperMarket/sfp3.webp",
        alt: "Supermarket & Furnished Apt - plan 3",
      },
      {
        kind: "image",
        src: "/images/SuperMarket/sfp4.webp",
        alt: "Supermarket & Furnished Apt - plan 4",
      },
      {
        kind: "image",
        src: "/images/SuperMarket/sfp5.webp",
        alt: "Supermarket & Furnished Apt - plan 5",
      },
    ],
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d1255.881928887047!2d-13.27604744112778!3d8.47131548861133!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sar!2slb!4v1701023477215!5m2!1sar!2slb",
  },
  {
    slug: "sierra-hotel",
    title: "Sierra Hotel",
    cover: "/images/Sierra-Hotel/sierra-hotel.webp",
    year: "2017",
    location: "Sierra Leone",
    client: "D&B Construction Limited",
    type: "Hotels",
    status: "preliminary Design",
    gallery: [
      "/images/Sierra-Hotel/sh1.webp",
      "/images/Sierra-Hotel/sh2.webp",
      "/images/Sierra-Hotel/sh3.webp",
      "/images/Sierra-Hotel/sh4.webp",
      "/images/Sierra-Hotel/sh5.webp",
      "/images/Sierra-Hotel/sh6.webp",
    ],
    plansMedia: [
      {
        kind: "image",
        src: "/images/Sierra-Hotel/shp1.webp",
        alt: "Sierra Hotel - plan 1",
      },
      {
        kind: "image",
        src: "/images/Sierra-Hotel/shp2.webp",
        alt: "Sierra Hotel - plan 2",
      },
      {
        kind: "image",
        src: "/images/Sierra-Hotel/shp3.webp",
        alt: "Sierra Hotel - plan 3",
      },
      {
        kind: "image",
        src: "/images/Sierra-Hotel/shp4.webp",
        alt: "Sierra Hotel - plan 4",
      },
      {
        kind: "image",
        src: "/images/Sierra-Hotel/shp5.webp",
        alt: "Sierra Hotel - plan 5",
      },
      {
        kind: "image",
        src: "/images/Sierra-Hotel/shp6.webp",
        alt: "Sierra Hotel - plan 6",
      },
      {
        kind: "image",
        src: "/images/Sierra-Hotel/shp7.webp",
        alt: "Sierra Hotel - plan 7",
      },
      {
        kind: "image",
        src: "/images/Sierra-Hotel/shp8.webp",
        alt: "Sierra Hotel - plan 8",
      },
      {
        kind: "image",
        src: "/images/Sierra-Hotel/shp9.webp",
        alt: "Sierra Hotel - plan 9",
      },
      {
        kind: "image",
        src: "/images/Sierra-Hotel/shp10.webp",
        alt: "Sierra Hotel - plan 10",
      },
    ],
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d1993.5958550703072!2d-13.28069435046224!3d8.469989016455505!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sar!2slb!4v1701022781245!5m2!1sar!2slb",
  },
  {
    slug: "gabon-residential-complex",
    title: "Gabon Residential Complex",
    cover: "/images/Gabon-Residential/gabon-residential-complex.webp",
    year: "2017",
    location: "Liberville -Gabon",
    client: "H.Khatib",
    type: "Residential building",
    status: "preliminary Design",
    gallery: [
      "/images/Gabon-Residential/G1.webp",
      "/images/Gabon-Residential/G2.webp",
      "/images/Gabon-Residential/G3.webp",
    ],
    plansMedia: [
      {
        kind: "image",
        src: "/images/Gabon-Residential/Gp1.webp",
        alt: "Gabon Residential Complex - plan 1",
      },
      {
        kind: "image",
        src: "/images/Gabon-Residential/g2p.webp",
        alt: "Gabon Residential Complex - plan 2",
      },
    ],
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d712.5958981703515!2d9.440892729752306!3d0.4135307442627607!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sar!2slb!4v1701013294710!5m2!1sar!2slb",
  },
  {
    slug: "apartement-18-20-30",
    title: "Apartement 18 - 20|30",
    cover: "/images/Apartment-18-20-20/ash1.webp",
    year: "2023",
    location: "Ashrafieh , Lebanon",
    client: "J&D Jabbour",
    type: "Interior Design build",
    status: "Delivered",
    gallery: [
      "/images/Apartment-18-20-20/ash1.webp",
      "/images/Apartment-18-20-20/ash2.webp",
      "/images/Apartment-18-20-20/ash3.webp",
      "/images/Apartment-18-20-20/ash4.webp",
      "/images/Apartment-18-20-20/ash5.webp",
      "/images/Apartment-18-20-20/ash6.webp",
      "/images/Apartment-18-20-20/ash7.webp",
      "/images/Apartment-18-20-20/ash8.webp",
      "/images/Apartment-18-20-20/ash9.webp",
      "/images/Apartment-18-20-20/ash10.webp",
      "/images/Apartment-18-20-20/ash11.webp",
      "/images/Apartment-18-20-20/ash12.webp",
      "/images/Apartment-18-20-20/ash13.webp",
      "/images/Apartment-18-20-20/ash14.webp",
      "/images/Apartment-18-20-20/ash15.webp",
      "/images/Apartment-18-20-20/ash16.webp",
      "/images/Apartment-18-20-20/ash17.webp",
      "/images/Apartment-18-20-20/ash18.webp",
    ],
    plansMedia: [
      {
        kind: "image",
        src: "/images/Apartment-18-20-20/ashp.webp",
        alt: "Apartement 18 - 20|30 - plan 1",
      },
    ],
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5627.791077860133!2d35.51644482288155!3d33.88997852627841!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x151f176d9839e407%3A0xb9cab7e9aa73630e!2sAchrafieh%202030!5e1!3m2!1sar!2slb!4v1701011733093!5m2!1sar!2slb",
  },
  {
    slug: "ali-hilal-complex",
    title: "AL Hilal Complex",
    cover: "/images/AL-Hilal-Complex/ali-hilal-complex.webp",
    year: "2022",
    location: "Fujaira , UAE",
    client: "Al Hilal Properties",
    type: "Residential Complex",
    status: "Preliminary Design",
    gallery: [
      "/images/AL-Hilal-Complex/ah1.webp",
      "/images/AL-Hilal-Complex/ah2.webp",
      "/images/AL-Hilal-Complex/ah3.webp",
      "/images/AL-Hilal-Complex/ah4.webp",
      "/images/AL-Hilal-Complex/ah5.webp",
    ],
    plansMedia: [
      {
        kind: "image",
        src: "/images/AL-Hilal-Complex/ahp1.webp",
        alt: "ALI Hilal Complex - plan1",
      },

      {
        kind: "image",
        src: "/images/AL-Hilal-Complex/ahp4.webp",
        alt: "ALI Hilal Complex - plan4",
      },
      {
        kind: "image",
        src: "/images/AL-Hilal-Complex/sec1.webp",
        alt: "ALI Hilal Complex - plan5",
      },
      {
        kind: "image",
        src: "/images/AL-Hilal-Complex/sec2.webp",
        alt: "ALI Hilal Complex - plan5",
      },
      {
        kind: "image",
        src: "/images/AL-Hilal-Complex/sec3.webp",
        alt: "ALI Hilal Complex - plan5",
      },
    ],
  },
];
